import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Users, MessageSquare, PlusCircle, HelpCircle, Sparkles, AlertTriangle,
  Search as SearchIcon, Flag, Trash2, Store,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_META = {
  question:       { label: "Question",        icon: HelpCircle,     color: "text-sky-700 bg-sky-50 border-sky-200" },
  recommendation: { label: "Recommendation",  icon: Sparkles,       color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  alert:          { label: "Alert",           icon: AlertTriangle,  color: "text-amber-700 bg-amber-50 border-amber-200" },
  lost_found:     { label: "Lost & Found",    icon: SearchIcon,     color: "text-violet-700 bg-violet-50 border-violet-200" },
};

export default function Neighborhood() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [mine, setMine] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [hint, setHint] = useState("");
  const [activeGid, setActiveGid] = useState(null);
  const [posts, setPosts] = useState([]);
  const [postsTotal, setPostsTotal] = useState(0);
  const [category, setCategory] = useState("all");
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [expandedPost, setExpandedPost] = useState(null); // {post, comments}

  const refreshGroups = async () => {
    try {
      const [m, s] = await Promise.all([
        api.get("/neighborhoods/mine"),
        api.get("/neighborhoods/suggest"),
      ]);
      const mineList = m.data?.items || [];
      setMine(mineList);
      setSuggested(s.data?.items || []);
      setHint(s.data?.hint || "");
      if (!activeGid && mineList.length) setActiveGid(mineList[0].id);
    } catch (e) {
      // If user isn't a customer, backend returns 403 — bail silently, the page
      // gates itself below.
    }
  };

  useEffect(() => { if (user?.role === "customer") refreshGroups(); }, [user?.id]);

  useEffect(() => {
    if (!activeGid) { setPosts([]); return; }
    let alive = true;
    (async () => {
      setLoadingPosts(true);
      try {
        const qs = category !== "all" ? `?category=${category}` : "";
        const r = await api.get(`/neighborhoods/${activeGid}/posts${qs}`);
        if (!alive) return;
        setPosts(r.data?.items || []);
        setPostsTotal(r.data?.total || 0);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        if (alive) setLoadingPosts(false);
      }
    })();
    return () => { alive = false; };
  }, [activeGid, category]);

  // Deep-linked post: /neighborhood?post=<pid>
  const deeplinkPid = params.get("post");
  useEffect(() => {
    if (!deeplinkPid || !user) return;
    (async () => {
      try {
        const r = await api.get(`/neighborhoods/posts/${deeplinkPid}`);
        setExpandedPost(r.data);
        setActiveGid(r.data?.post?.group_id || activeGid);
      } catch (e) {
        // Post no longer exists (deleted, hidden) — swallow silently.
      }
    })();
  }, [deeplinkPid, user]);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-20">
        <div className="rounded-md border border-border p-8 text-center">
          <Users className="mx-auto mb-3 text-[#B31E1E]" size={32} />
          <h1 className="font-display text-3xl">Neighborhood boards</h1>
          <p className="mt-2 text-gray-600">Sign in to browse and post in your neighborhood.</p>
          <div className="mt-6">
            <Link to="/login"><Button className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white">Log in</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  if (user.role !== "customer") {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-20">
        <div className="rounded-md border border-border p-8 text-center">
          <Users className="mx-auto mb-3 text-gray-400" size={32} />
          <h1 className="font-display text-2xl">Neighborhood boards are for customers</h1>
          <p className="mt-2 text-gray-600">Businesses interact through profiles, reviews and promotions — the neighborhood board is a customer-only space.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-10" data-testid="neighborhood-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-[#B31E1E]">Community</div>
          <h1 className="font-display text-4xl tracking-tighter mt-1">Your neighborhoods</h1>
          <p className="mt-2 text-gray-600 max-w-2xl">
            Ask a question, recommend a business, post an alert or a lost-and-found — visible only to neighbors you share a group with.
          </p>
        </div>
        <CreateNeighborhoodDialog onCreated={refreshGroups} defaults={{ city: user.city || "", state: user.state || "", zip: user.zip || "" }} />
      </header>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        <aside className="space-y-6">
          <GroupList
            heading="My groups"
            items={mine}
            activeGid={activeGid}
            onSelect={setActiveGid}
            onLeave={async (g) => {
              if (!confirm(`Leave "${g.name}"? You can rejoin anytime.`)) return;
              try {
                await api.post(`/neighborhoods/${g.id}/leave`);
                toast.success(`Left ${g.name}`);
                if (activeGid === g.id) setActiveGid(null);
                refreshGroups();
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            }}
            emptyMessage="You haven't joined any neighborhood yet."
            testid="my-groups"
          />
          <SuggestedGroups
            items={suggested.filter(g => !g.joined)}
            hint={hint}
            onJoined={refreshGroups}
          />
        </aside>

        <section className="min-w-0">
          {!activeGid ? (
            <Card className="rounded-md p-10 text-center text-gray-600" data-testid="neighborhood-empty">
              <Users className="mx-auto mb-2 text-gray-400" size={28} />
              Join or create a neighborhood to see posts.
            </Card>
          ) : (
            <>
              <FeedHeader
                group={[...mine, ...suggested].find(g => g.id === activeGid)}
                category={category}
                onCategoryChange={setCategory}
                onNewPost={() => setExpandedPost({ __new: true })}
                onJoin={async (gid) => {
                  try {
                    await api.post(`/neighborhoods/${gid}/join`);
                    toast.success("Joined");
                    refreshGroups();
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                }}
                onLeave={async (g) => {
                  if (!confirm(`Leave "${g.name}"? You can rejoin anytime.`)) return;
                  try {
                    await api.post(`/neighborhoods/${g.id}/leave`);
                    toast.success(`Left ${g.name}`);
                    setActiveGid(null);
                    refreshGroups();
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                }}
              />
              {loadingPosts ? (
                <p className="text-sm text-gray-500">Loading…</p>
              ) : posts.length === 0 ? (
                <Card className="rounded-md p-10 text-center text-gray-600" data-testid="feed-empty">
                  No posts yet — be the first to post.
                </Card>
              ) : (
                <div className="space-y-3" data-testid="feed-list">
                  {posts.map((p) => (
                    <PostCard
                      key={p.id}
                      post={p}
                      onOpen={async () => {
                        try {
                          const r = await api.get(`/neighborhoods/posts/${p.id}`);
                          setExpandedPost(r.data);
                        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                      }}
                    />
                  ))}
                  <div className="text-xs font-mono text-gray-400 pt-2">Showing {posts.length} of {postsTotal}</div>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Post detail / new post modal */}
      <Dialog
        open={!!expandedPost}
        onOpenChange={(o) => { if (!o) setExpandedPost(null); }}
      >
        <DialogContent className="max-w-2xl rounded-md" data-testid="post-detail-dialog">
          {expandedPost?.__new ? (
            <NewPostForm
              groupId={activeGid}
              onDone={async (created) => {
                setExpandedPost(null);
                // Optimistic prepend
                if (created) setPosts((prev) => [created, ...prev]);
              }}
            />
          ) : expandedPost?.post ? (
            <PostDetail
              data={expandedPost}
              currentUserId={user.id}
              onDeleted={(pid) => {
                setPosts((prev) => prev.filter(p => p.id !== pid));
                setExpandedPost(null);
              }}
              onCommented={(comment) => {
                setExpandedPost((prev) => prev ? { ...prev, comments: [...prev.comments, comment] } : prev);
                setPosts((prev) => prev.map(p => p.id === expandedPost.post.id ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p));
              }}
              onCommentDeleted={(cid) => {
                setExpandedPost((prev) => prev ? { ...prev, comments: prev.comments.filter(c => c.id !== cid) } : prev);
                setPosts((prev) => prev.map(p => p.id === expandedPost.post.id ? { ...p, comment_count: Math.max(0, (p.comment_count || 1) - 1) } : p));
              }}
              onFlaggedHidden={(pid) => {
                setPosts((prev) => prev.filter(p => p.id !== pid));
                setExpandedPost(null);
                toast.success("Post reported and auto-hidden.");
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ------------------------------ Sub-components ----------------------------- */

function GroupList({ heading, items, activeGid, onSelect, onLeave, emptyMessage, testid }) {
  return (
    <div>
      <div className="overline text-gray-500 mb-2">{heading}</div>
      {items.length === 0 ? (
        <p className="text-sm text-gray-500">{emptyMessage}</p>
      ) : (
        <ul className="space-y-1" data-testid={testid}>
          {items.map((g) => (
            <li key={g.id} className="group relative">
              <button
                onClick={() => onSelect(g.id)}
                className={`w-full text-left pl-3 pr-14 py-2 rounded-md text-sm border ${activeGid === g.id ? "border-[#B31E1E] bg-[#B31E1E]/5 font-medium" : "border-transparent hover:border-border"}`}
                data-testid={`group-item-${g.id}`}
              >
                <div className="truncate">{g.name}</div>
                <div className="text-[11px] text-gray-500 truncate">
                  {g.city}{g.state ? `, ${g.state}` : ""} · {g.member_count} member{g.member_count === 1 ? "" : "s"}
                </div>
              </button>
              {/* Leave button — visible on hover so the row stays quiet by
                  default. Placed absolutely so it doesn't affect the button's
                  clickable area. */}
              {onLeave && (
                <button
                  onClick={(e) => { e.stopPropagation(); onLeave(g); }}
                  className="absolute top-1/2 -translate-y-1/2 right-2 rounded-sm text-[10px] font-mono uppercase tracking-wider text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity px-1.5 py-1"
                  aria-label={`Leave ${g.name}`}
                  data-testid={`leave-${g.id}`}
                >
                  Leave
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SuggestedGroups({ items, hint, onJoined }) {
  if (!items || items.length === 0) {
    return (
      <div>
        <div className="overline text-gray-500 mb-2">Suggested nearby</div>
        <p className="text-xs text-gray-500">{hint || "No suggestions yet. Create the first neighborhood for your street."}</p>
      </div>
    );
  }
  const join = async (gid) => {
    try {
      await api.post(`/neighborhoods/${gid}/join`);
      toast.success("Joined");
      onJoined();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="overline text-gray-500 mb-2">Suggested nearby</div>
      <ul className="space-y-1" data-testid="suggested-groups">
        {items.map((g) => (
          <li key={g.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-border">
            <div className="min-w-0">
              <div className="text-sm truncate">{g.name}</div>
              <div className="text-[11px] text-gray-500 truncate">{g.city}{g.state ? `, ${g.state}` : ""} · {g.member_count}</div>
            </div>
            <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => join(g.id)} data-testid={`join-${g.id}`}>Join</Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FeedHeader({ group, category, onCategoryChange, onNewPost, onJoin, onLeave }) {
  if (!group) return null;
  return (
    <div className="mb-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="font-display text-2xl truncate">{group.name}</h2>
          <p className="text-xs text-gray-500 truncate">
            {group.city}{group.state ? `, ${group.state}` : ""} · {group.member_count} member{group.member_count === 1 ? "" : "s"} · {group.post_count} post{group.post_count === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Join / Leave toggle — a member sees "Leave", a non-member sees
              "Join". Sits next to New post so it's the first thing you notice
              when viewing a group. */}
          {group.joined ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onLeave && onLeave(group)}
              className="rounded-[35px] text-red-600 hover:text-red-700 border-red-200 hover:border-red-400"
              data-testid={`feed-leave-${group.id}`}
            >
              Leave
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => onJoin && onJoin(group.id)}
              className="rounded-[35px] bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid={`feed-join-${group.id}`}
            >
              Join this group
            </Button>
          )}
          <Button onClick={onNewPost} disabled={!group.joined} className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white disabled:opacity-50" data-testid="new-post-button">
            <PlusCircle size={14} className="mr-2" /> New post
          </Button>
        </div>
      </div>
      <Tabs value={category} onValueChange={onCategoryChange} className="mt-4">
        <TabsList className="rounded-sm">
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          {Object.entries(CATEGORY_META).map(([k, v]) => (
            <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}>{v.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
}

function PostCard({ post, onOpen }) {
  const meta = CATEGORY_META[post.category] || CATEGORY_META.question;
  const Icon = meta.icon;
  return (
    <Card
      className="rounded-md p-4 hover:border-[#B31E1E]/40 transition-colors cursor-pointer"
      onClick={onOpen}
      data-testid={`post-${post.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className={`rounded-sm text-[11px] ${meta.color}`}>
              <Icon size={11} className="mr-1" /> {meta.label}
            </Badge>
            <span className="text-[11px] text-gray-500">by {post.author_name || "Neighbor"}</span>
          </div>
          <h3 className="font-medium text-gray-900 truncate">{post.title}</h3>
          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{post.body}</p>
          {post.business_name ? (
            <div className="mt-2 text-xs text-gray-500 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1">
              <Store size={11} /> {post.business_name}
            </div>
          ) : null}
        </div>
        <div className="text-right shrink-0">
          <div className="text-[11px] text-gray-500 whitespace-nowrap">{new Date(post.created_at).toLocaleDateString()}</div>
          <div className="text-xs text-gray-700 mt-2 inline-flex items-center gap-1">
            <MessageSquare size={12} /> {post.comment_count || 0}
          </div>
        </div>
      </div>
    </Card>
  );
}

function CreateNeighborhoodDialog({ onCreated, defaults }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [city, setCity] = useState(defaults?.city || "");
  const [state, setState] = useState(defaults?.state || "");
  const [zips, setZips] = useState(defaults?.zip || "");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (name.trim().length < 2) return toast.error("Neighborhood name is required.");
    if (!city.trim() || !state.trim()) return toast.error("City and state are required.");
    setSaving(true);
    try {
      const zip_codes = zips.split(",").map(z => z.trim()).filter(Boolean);
      const r = await api.post("/neighborhoods", {
        name: name.trim(),
        city: city.trim(),
        state: state.trim(),
        zip_codes,
        description: description.trim(),
      });
      toast.success(r.data.created ? "Neighborhood created" : "Joined existing neighborhood");
      setOpen(false);
      setName(""); setDescription("");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-[35px]" data-testid="create-neighborhood-button">
          <PlusCircle size={14} className="mr-2" /> Create neighborhood
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg rounded-md">
        <DialogHeader>
          <DialogTitle>Create a neighborhood</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="nb-name" className="text-xs">Name</Label>
            <Input id="nb-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Elmwood, Rockridge, Nob Hill" data-testid="nb-name-input" className="rounded-sm mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="nb-city" className="text-xs">City</Label>
              <Input id="nb-city" value={city} onChange={(e) => setCity(e.target.value)} data-testid="nb-city-input" className="rounded-sm mt-1" />
            </div>
            <div>
              <Label htmlFor="nb-state" className="text-xs">State</Label>
              <Input id="nb-state" value={state} onChange={(e) => setState(e.target.value)} data-testid="nb-state-input" className="rounded-sm mt-1" />
            </div>
          </div>
          <div>
            <Label htmlFor="nb-zips" className="text-xs">ZIP codes (comma-separated, optional)</Label>
            <Input id="nb-zips" value={zips} onChange={(e) => setZips(e.target.value)} placeholder="94702, 94703" data-testid="nb-zips-input" className="rounded-sm mt-1" />
          </div>
          <div>
            <Label htmlFor="nb-desc" className="text-xs">Description (optional)</Label>
            <Textarea id="nb-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="nb-desc-input" className="rounded-sm mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white"
            data-testid="nb-create-submit"
          >
            {saving ? "Saving…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewPostForm({ groupId, onDone }) {
  const [category, setCategory] = useState("question");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [bizQuery, setBizQuery] = useState("");
  const [bizResults, setBizResults] = useState([]);
  const [bizPicked, setBizPicked] = useState(null); // {id, name}
  const [saving, setSaving] = useState(false);

  // Simple business search (debounced)
  useEffect(() => {
    if (category !== "recommendation") return;
    if (!bizQuery || bizQuery.trim().length < 2) { setBizResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/businesses?q=${encodeURIComponent(bizQuery)}&limit=6`);
        const items = r.data?.items || r.data || [];
        setBizResults(items.slice(0, 6));
      } catch { setBizResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [bizQuery, category]);

  const submit = async () => {
    if (title.trim().length < 3) return toast.error("Title is required.");
    if (body.trim().length < 3) return toast.error("Post body is required.");
    setSaving(true);
    try {
      const r = await api.post(`/neighborhoods/${groupId}/posts`, {
        category, title: title.trim(), body: body.trim(),
        business_id: category === "recommendation" ? bizPicked?.id : null,
      });
      toast.success("Posted");
      onDone(r.data?.post);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <DialogHeader><DialogTitle>New post</DialogTitle></DialogHeader>
      <div>
        <Label className="text-xs">Category</Label>
        <Select value={category} onValueChange={(v) => { setCategory(v); if (v !== "recommendation") setBizPicked(null); }}>
          <SelectTrigger className="rounded-sm mt-1" data-testid="new-post-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(CATEGORY_META).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Title</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short and specific" data-testid="new-post-title" className="rounded-sm mt-1" />
      </div>
      <div>
        <Label className="text-xs">Details</Label>
        <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} data-testid="new-post-body" className="rounded-sm mt-1" />
      </div>
      {category === "recommendation" && (
        <div>
          <Label className="text-xs">Tag a business (optional)</Label>
          {bizPicked ? (
            <div className="mt-1 flex items-center justify-between rounded-sm border border-border px-3 py-2 text-sm" data-testid="new-post-picked-biz">
              <span>{bizPicked.name}</span>
              <button onClick={() => setBizPicked(null)} className="text-xs text-gray-500 hover:text-red-600">Remove</button>
            </div>
          ) : (
            <>
              <Input value={bizQuery} onChange={(e) => setBizQuery(e.target.value)} placeholder="Search Servrevu businesses" data-testid="new-post-biz-search" className="rounded-sm mt-1" />
              {bizResults.length > 0 && (
                <div className="mt-1 border border-border rounded-sm max-h-40 overflow-auto">
                  {bizResults.map((b) => (
                    <button
                      key={b.id}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                      onClick={() => { setBizPicked({ id: b.id, name: b.name || b.business_name }); setBizQuery(""); setBizResults([]); }}
                      data-testid={`new-post-biz-pick-${b.id}`}
                    >
                      {b.name || b.business_name} <span className="text-xs text-gray-500">{b.city ? `· ${b.city}` : ""}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
      <DialogFooter>
        <Button variant="ghost" onClick={() => onDone(null)}>Cancel</Button>
        <Button onClick={submit} disabled={saving} className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid="new-post-submit">
          {saving ? "Posting…" : "Post"}
        </Button>
      </DialogFooter>
    </div>
  );
}

function PostDetail({ data, currentUserId, onDeleted, onCommented, onCommentDeleted, onFlaggedHidden }) {
  const { post, comments = [] } = data;
  const meta = CATEGORY_META[post.category] || CATEGORY_META.question;
  const Icon = meta.icon;
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const isMine = post.author_id === currentUserId;

  const submitReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const r = await api.post(`/neighborhoods/posts/${post.id}/comments`, { body: reply.trim() });
      onCommented(r.data.comment);
      setReply("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  const flag = async () => {
    if (!confirm("Report this post?")) return;
    try {
      const r = await api.post(`/neighborhoods/posts/${post.id}/flag`);
      if (r.data?.hidden) onFlaggedHidden(post.id);
      else toast.success("Reported. Thanks.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deletePost = async () => {
    if (!confirm("Delete this post?")) return;
    try {
      await api.delete(`/neighborhoods/posts/${post.id}`);
      onDeleted(post.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="post-detail">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Icon size={14} className="text-gray-500" /> {post.title}
        </DialogTitle>
      </DialogHeader>
      <div className="flex items-center gap-2 text-[11px] text-gray-500">
        <Badge variant="outline" className={`rounded-sm ${meta.color}`}>{meta.label}</Badge>
        <span>by {post.author_name || "Neighbor"}</span>
        <span>· {new Date(post.created_at).toLocaleString()}</span>
      </div>
      <p className="text-sm text-gray-800 whitespace-pre-wrap">{post.body}</p>
      {post.business_name && (
        <div className="text-xs text-gray-600 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1">
          <Store size={11} /> {post.business_name}
          {post.business_id && <Link to={`/businesses/${post.business_id}`} className="ml-2 text-[#B31E1E] hover:underline">View</Link>}
        </div>
      )}
      <div className="flex items-center gap-2 border-t border-border pt-3">
        {isMine ? (
          <Button size="sm" variant="ghost" onClick={deletePost} className="text-red-600 hover:text-red-700" data-testid="delete-post-button">
            <Trash2 size={13} className="mr-1" /> Delete
          </Button>
        ) : (
          <Button size="sm" variant="ghost" onClick={flag} data-testid="flag-post-button">
            <Flag size={13} className="mr-1" /> Report
          </Button>
        )}
      </div>

      <div>
        <div className="overline text-gray-500 mb-2">{comments.length} {comments.length === 1 ? "reply" : "replies"}</div>
        <div className="space-y-2 max-h-72 overflow-auto pr-1" data-testid="comments-list">
          {comments.map((c) => (
            <CommentRow
              key={c.id}
              comment={c}
              currentUserId={currentUserId}
              onDeleted={() => onCommentDeleted(c.id)}
            />
          ))}
        </div>
      </div>

      <div className="border-t border-border pt-3">
        <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" data-testid="reply-input" className="rounded-sm" />
        <div className="mt-2 flex justify-end">
          <Button onClick={submitReply} disabled={sending || !reply.trim()} className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid="reply-submit">
            {sending ? "Sending…" : "Post reply"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CommentRow({ comment, currentUserId, onDeleted }) {
  const isMine = comment.author_id === currentUserId;
  const flag = async () => {
    if (!confirm("Report this reply?")) return;
    try {
      const r = await api.post(`/neighborhoods/comments/${comment.id}/flag`);
      if (r.data?.hidden) { onDeleted(); toast.success("Reply reported and hidden."); }
      else toast.success("Reported. Thanks.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async () => {
    if (!confirm("Delete this reply?")) return;
    try { await api.delete(`/neighborhoods/comments/${comment.id}`); onDeleted(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-md border border-border p-3" data-testid={`comment-${comment.id}`}>
      <div className="text-xs text-gray-500 flex items-center justify-between">
        <span>{comment.author_name || "Neighbor"} · {new Date(comment.created_at).toLocaleString()}</span>
        {isMine ? (
          <button onClick={del} className="text-red-600 hover:underline" data-testid={`delete-comment-${comment.id}`}>Delete</button>
        ) : (
          <button onClick={flag} className="text-gray-500 hover:text-amber-600" data-testid={`flag-comment-${comment.id}`}>Report</button>
        )}
      </div>
      <p className="text-sm text-gray-800 whitespace-pre-wrap mt-1">{comment.body}</p>
    </div>
  );
}
